import 'react-autocomplete-input/dist/bundle.css';

import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useCallback, useState } from 'react';
import { IconButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { Popover, Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { OperatorsIcon } from 'src/svgs/OperatorsIcon';
import { FORMULA_OPERATORS_AND_NUMBERS_REGEXP_START } from 'src/utils/columns/customColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { MATH_OPERATOR_TO_CID } from 'src/utils/constants/contentIdMaps';
import { Feature } from 'src/WebApiController';

import * as styles from '../../TableColumns.css';
import { MathOperator } from '../TableCustomColumnModal.types';
import * as inputStyles from './FormulaBuilderInput.css';

type FormulaBuilderShortcutsProps = {
  fieldOptionsReverse: Record<string, string>;
  formulaTokens: string[];
  setFormulaTokens: (tokens: string[]) => void;
  formulaCaretPosition: number;
  setFormulaCaretPosition: (position: number) => void;
};

/**
 * Formula shortcuts, current hidden with styles.formulaTokenButtonsWrapper
 */
export const FormulaBuilderShortcuts = ({
  fieldOptionsReverse,
  formulaTokens,
  setFormulaTokens,
  formulaCaretPosition,
  setFormulaCaretPosition,
}: FormulaBuilderShortcutsProps) => {
  const hasMinMaxInCustomColumnFeature = useUserHasFeature(
    Feature.MinMaxInCustomColumn
  );
  const formulaTokensWithCaret = useMemo(() => {
    if (formulaCaretPosition === -1) return formulaTokens;
    const result = [...formulaTokens];
    result.splice(formulaCaretPosition, 0, '|');
    return result;
  }, [formulaCaretPosition, formulaTokens]);

  const autoCompleteOptions = useMemo(
    () => Object.keys(fieldOptionsReverse),
    [fieldOptionsReverse]
  );
  return (
    <div className={styles.formulaTokenButtonsWrapper}>
      <PosDropdown
        trigger={
          <Button variant="text" style={{ height: '38px' }}>
            <div style={{ width: vars.iconSize.xl }}>
              <IconButton icon={<OperatorsIcon />}></IconButton>
            </div>
          </Button>
        }
        style={{
          display: 'block',
          margin: 'auto',
          padding: vars.spacing.sm,
        }}
      >
        {['+', '-', '*', '/', '(', ')'].map((label, i) => (
          <div
            key={i}
            className={styles.tokenButton}
            onClick={(e) => {
              e.stopPropagation();
              // add to tokens at caret position
              const newTokens = [...formulaTokensWithCaret].map((token) =>
                token === '|' ? label : token
              );
              setFormulaTokens(newTokens);
              setFormulaCaretPosition(formulaCaretPosition + 1);
            }}
          >
            {label}
          </div>
        ))}
        {hasMinMaxInCustomColumnFeature &&
          ['min', 'max'].map((func, i) => (
            <div
              key={`func-${i}`}
              className={styles.tokenButton}
              onClick={(e) => {
                e.stopPropagation();
                // Insert function structure: func(a, b)
                // Replace caret with: func, (, ), with caret positioned after (
                const tokensBeforeCaret = formulaTokens.slice(
                  0,
                  formulaCaretPosition
                );
                const tokensAfterCaret =
                  formulaTokens.slice(formulaCaretPosition);
                const newTokens = [
                  ...tokensBeforeCaret,
                  func,
                  '(',
                  ',',
                  ')',
                  ...tokensAfterCaret,
                ];
                setFormulaTokens(newTokens);
                // Position caret after the opening parenthesis
                setFormulaCaretPosition(formulaCaretPosition + 4);
              }}
            >
              {func}
            </div>
          ))}
      </PosDropdown>
      <PosDropdown
        trigger={
          <Button variant="text">
            <Content id={ContentId.Metrics} />
          </Button>
        }
        style={{
          display: 'block',
          margin: 'auto',
          padding: vars.spacing.sm,
          width: '40%',
        }}
      >
        {autoCompleteOptions.map((label, i) => (
          <div
            key={i}
            className={styles.tokenButton}
            onClick={(e) => {
              e.stopPropagation();
              const tokenToAdd = fieldOptionsReverse[label] ?? '|';
              // add to tokens at caret position
              const newTokens = [...formulaTokensWithCaret].map((token) =>
                token === '|' ? tokenToAdd : token
              );
              setFormulaTokens(newTokens);
              setFormulaCaretPosition(formulaCaretPosition + 1);
            }}
          >
            {label}
          </div>
        ))}
      </PosDropdown>
    </div>
  );
};

export const FormulaTokenDisplay = ({
  index,
  elementRef,
  token,
  formulaTokens,
  formulaCaretPosition,
  fieldOptionsReverse,
  setFormulaCaretPosition,
  setFormulaTokens,
  showMetricSelector,
  setShowMetricSelector,
  showMathOperatorSelector,
  setShowMathOperatorSelector,
}: {
  index: number;
  elementRef: (el: HTMLSpanElement) => void;
  token: string;
  formulaTokens: string[];
  formulaCaretPosition: number;
  fieldOptionsReverse: Record<string, string>;
  setFormulaTokens: (_: string[]) => void;
  setFormulaCaretPosition: (_: number) => void;
  showMetricSelector?: boolean;
  setShowMetricSelector: (_: boolean) => void;
  showMathOperatorSelector?: boolean;
  setShowMathOperatorSelector: (_: boolean) => void;
}) => {
  const hasMinMaxInCustomColumnFeature = useUserHasFeature(
    Feature.MinMaxInCustomColumn
  );
  const autoCompleteOptions = useMemo(
    () =>
      Object.keys(fieldOptionsReverse).reduce(
        (options, current) => ({
          ...options,
          [current]: current,
        }),
        {} as Record<string, string>
      ),
    [fieldOptionsReverse]
  );

  const formulaTokensWithCaret = useMemo(() => {
    if (formulaCaretPosition === -1) return formulaTokens;
    const result = [...formulaTokens];
    result.splice(formulaCaretPosition, 0, '|');
    return result;
  }, [formulaCaretPosition, formulaTokens]);

  const fieldOptionsDisplay = useMemo(() => {
    const options: Record<string, string> = {};
    Object.keys(fieldOptionsReverse).forEach((key) => {
      options[fieldOptionsReverse[key]] = key;
    });
    return options;
  }, [fieldOptionsReverse]);

  const onSelectMetrics = useCallback(
    (metric: string | null) => {
      if (!metric) return;
      const metricToken = fieldOptionsReverse[metric];
      if (!metricToken) return;
      const newTokens = [...formulaTokensWithCaret].map((token) =>
        token === '|' ? metricToken : token
      );
      setFormulaTokens(newTokens);
      setFormulaCaretPosition(formulaCaretPosition + 1);
      setShowMetricSelector(false);
    },
    [
      fieldOptionsReverse,
      formulaCaretPosition,
      formulaTokensWithCaret,
      setFormulaCaretPosition,
      setFormulaTokens,
      setShowMetricSelector,
    ]
  );

  if (token !== '|') {
    return (
      <span
        ref={elementRef}
        className={styles.formulaToken}
        onClick={(e) => {
          e.stopPropagation();
          setFormulaCaretPosition(index + 1);
        }}
      >
        {FORMULA_OPERATORS_AND_NUMBERS_REGEXP_START.test(token)
          ? token
          : fieldOptionsDisplay[token]}
      </span>
    );
  }

  if (showMetricSelector) {
    return (
      <PosEnumSelect
        variant="textPlain"
        shape="none"
        rootProps={{
          defaultOpen: true,
        }}
        onChange={onSelectMetrics}
        searchable
        valueOptionsContent={autoCompleteOptions}
      />
    );
  }
  if (hasMinMaxInCustomColumnFeature && showMathOperatorSelector) {
    return (
      <MathOperatorSelector
        autoCompleteOptions={autoCompleteOptions}
        fieldOptionsReverse={fieldOptionsReverse}
        onAddMathOperator={(mathOperatorTokens: string[]) => {
          const updatedTokens = [...formulaTokens, ...mathOperatorTokens];
          setFormulaTokens(updatedTokens);
          setFormulaCaretPosition(updatedTokens.length);
          setShowMathOperatorSelector(false);
        }}
        onClose={() => setShowMathOperatorSelector(false)}
      />
    );
  }
  return (
    <div key={token} className={styles.caret}>
      |
    </div>
  );
};

const MathOperatorSelector = ({
  onAddMathOperator,
  autoCompleteOptions,
  fieldOptionsReverse,
  onClose,
}: {
  onAddMathOperator: (tokens: string[]) => void;
  autoCompleteOptions: Record<string, string>;
  fieldOptionsReverse: Record<string, string>;
  onClose: () => void;
}) => {
  const [operator, setOperator] = useState<MathOperator | null>(null);
  const [metrics, setMetrics] = useState<string[]>([]);

  const onSaveMathOperator = useCallback(() => {
    if (!operator || isEmpty(metrics)) {
      return;
    }

    const newTokens: string[] = [operator.toLowerCase()];
    newTokens.push('(');
    newTokens.push(
      ...metrics.flatMap((metric, index) => {
        const metricToken = fieldOptionsReverse[metric];
        if (!metricToken) return [];

        return index === 0 ? metricToken : [',', metricToken];
      })
    );
    newTokens.push(')');

    onAddMathOperator(newTokens);
  }, [operator, metrics, onAddMathOperator, fieldOptionsReverse]);

  const readyToSave = useMemo(
    () => operator && !isEmpty(metrics),
    [metrics, operator]
  );

  return (
    <Popover.Root open>
      <Popover.Trigger asChild>
        <span></span>
      </Popover.Trigger>
      <Popover.Content align="start" onClick={(e) => e.stopPropagation()}>
        <Stack
          direction="column"
          gap="m"
          className={inputStyles.mathOperatorSelectorContainer}
        >
          <PosEnumSelect
            value={operator}
            placeholderText={'Select Operator'}
            onChange={(updated) => setOperator(updated)}
            valueOptionsContent={MATH_OPERATOR_TO_CID}
          />
          <PosMultiSelect
            triggerProps={{ style: { width: '100%' } }}
            values={metrics}
            searchable={true}
            onChange={({ newValues }) => setMetrics(newValues)}
            valueOptionsContent={autoCompleteOptions}
          />
          <Stack justifyContent="spaceBetween">
            <OkButton
              variant="text"
              textContentId={ContentId.Close}
              onClick={onClose}
            />
            <OkButton
              textContentId={ContentId.Save}
              disabled={!readyToSave}
              onClick={onSaveMathOperator}
            />
          </Stack>
        </Stack>
      </Popover.Content>
    </Popover.Root>
  );
};
